import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase, readableError } from './supabase'
import { Button } from '../components/ui'

/**
 * Deleting a song, in one place, because two screens do it.
 *
 * The delete itself is `archive_delete_song()`, never a `delete from songs`.
 * To the writer that changes nothing — the song leaves the screen the moment
 * they confirm — but underneath the row is copied into a thirty-day archive
 * and, crucially, the recording is queued for deletion by date instead of
 * being orphaned. The old client-side delete removed the songs row, which was
 * the only pointer the R2 purge had, so every upload deleted before its hour
 * was up stayed in the bucket permanently.
 *
 * The dialog is a <dialog showModal>, which is the only thing that escapes
 * .screen's overflow and the fixed tab bar without inventing a z-index.
 */
export function useSongDelete(onDone) {
  const dlg = useRef(null)
  const [doomed, setDoomed] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    const d = dlg.current
    if (!d) return
    if (doomed && !d.open) d.showModal()
    if (!doomed && d.open) d.close()
  }, [doomed])

  const destroy = async () => {
    setBusy(true); setErr(null)
    const { error } = await supabase.rpc('archive_delete_song', { p_song: doomed.id })
    setBusy(false)
    setDoomed(null)
    if (error) return setErr(readableError(error))
    onDone?.()
  }

  const dialog = (
    <>
      {err && <p className="err">{err}</p>}
      <dialog ref={dlg} className="sheet" onClose={() => setDoomed(null)}
              onCancel={(e) => { e.preventDefault(); setDoomed(null) }}>
        <div className="sheet-body">
          <h2 className="q">Delete “{doomed?.title || 'Untitled'}”?</h2>
          <p className="sub">
            It goes from your songs straight away. Nobody else can see it, and
            you have thirty days to change your mind — after that the words,
            the recording and the splits are gone for good.
          </p>
          <p className="small">
            Older takes of the recording aren't kept during those thirty days;
            only the one that was the song.
          </p>

          <div className="hr" />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button busy={busy} className="btn btn-danger" onClick={destroy}>
              Delete
            </Button>
            <Button className="btn btn-quiet" disabled={busy}
                    onClick={() => setDoomed(null)}>
              Keep it
            </Button>
          </div>
        </div>
      </dialog>
    </>
  )

  return { ask: setDoomed, dialog, deleting: busy }
}

/**
 * The other half: what you deleted and haven't lost yet.
 *
 * Read straight from the archive table — a writer only ever sees their own
 * rows, and restoring is an RPC that puts the song and its co-writers back.
 * A restored song keeps the take that was the song; it does not get its
 * version history back, because those objects were released on the first
 * purge run rather than held for a month.
 */
export function useRecentlyDeleted() {
  const [rows, setRows] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('deleted_songs')
      .select('song_id, title, deleted_at, purge_after, reason')
      .is('restored_at', null)
      .order('deleted_at', { ascending: false })
    if (error) return setErr(readableError(error))
    setRows(data ?? [])
  }, [])

  useEffect(() => { load() }, [load])

  const restore = async (row) => {
    setBusyId(row.song_id); setErr(null)
    const { error } = await supabase.rpc('restore_deleted_song', { p_song: row.song_id })
    setBusyId(null)
    if (error) return setErr(readableError(error))
    await load()
    return true
  }

  return { rows: rows ?? [], loading: rows === null, restore, busyId, err, reload: load }
}

/** "27 days left", for a row in the recently-deleted list. */
export function daysLeft(purgeAfter) {
  const days = Math.ceil((new Date(purgeAfter) - Date.now()) / 86400000)
  if (days <= 0) return 'going today'
  if (days === 1) return '1 day left'
  return `${days} days left`
}
